import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Autocomplete,
  TextField,
  Button,
  IconButton,
  Backdrop,
  Chip,
  Typography,
  ClickAwayListener,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Tooltip from "@mui/material/Tooltip";
import { useAuth } from "oidc-react";
import { useTranslation } from "react-i18next";
import { Box, Grid } from "@mui/material";
import { useToastState } from "../ToastStateProvider";
import {
  addDays,
  startOfWeek,
  format,
  isWeekend,
  getISOWeek,
  set,
  isSameDay,
} from "date-fns";
import { parseError } from "../../errorUtils.js";

const ReportTimeModal = ({
  open,
  onClose,
  baseDate,
  editItem,
  onSave,
  reportedTimes,
  prefillData,
  onPrefillConsumed,
}) => {
  const { t, i18n } = useTranslation();
  const [quantity, setQuantity] = useState(1);
  const [selectedWageCode, setSelectedWageCode] = useState(null);
  const [wageCodes, setWageCodes] = useState([]);
  const [selectableWageCodes, setSelectableWageCodes] = useState([]);
  const [wageCodeTypes, setWageCodeTypes] = useState([]);
  const [selectedWageCodeType, setSelectedWageCodeType] = useState(null);
  const [selectedTimeReportType, setSelectedTimeReportType] = useState("wage");
  const [isWageTooltipOpen, setIsWageTooltipOpen] = useState(false);
  const [selectedDates, setSelectedDates] = useState([]);
  const [weekDays, setWeekDays] = useState([]);
  const [note, setNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isPrefillLocked, setIsPrefillLocked] = useState(false);
  const { setToastState } = useToastState();
  // Validation state
  const [showValidation, setShowValidation] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    dates: false,
    quantity: false,
    wageCodeType: false,
    wageCode: false,
    project: false,
    subProject: false,
    projectActivity: false,
    projectActivityCost: false,
  });

  const showToast = useCallback(
    (message, severity) => {
      setToastState({ message, severity, open: true });
    },
    [setToastState]
  );

  // Project-related states
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [subProjects, setSubProjects] = useState([]);
  const [selectedSubProject, setSelectedSubProject] = useState(null);
  const [projectActivities, setProjectActivities] = useState([]);
  const [selectedProjectActivity, setSelectedProjectActivity] = useState(null);
  const [projectActivityCosts, setProjectActivityCosts] = useState([]);
  const [selectedProjectActivityCost, setSelectedProjectActivityCost] =
    useState(null);
  const [message, setMessage] = useState("");

  const auth = useAuth();
  const accessToken = auth.userData?.access_token;
  const rootUrl = import.meta.env.VITE_BACKEND_API_ROOT_URL;

  const dayNames = [];
  const firstDayOfWeek = startOfWeek(baseDate, { weekStartsOn: 1 });
  for (let i = 1; i <= 7; i++) {
    const formatter = new Intl.DateTimeFormat(i18n.language, {
      weekday: "short",
    });
    const dayName = formatter.format(addDays(firstDayOfWeek, i - 1));
    dayNames.push(dayName);
  }

  const resetForm = () => {
    // Reset time report type to default "wage"
    setSelectedTimeReportType("wage");
    // Project-related fields
    setSelectedProject(null);
    setSelectedSubProject(null);
    setSelectedProjectActivity(null);
    setSelectedProjectActivityCost(null);
    setProjects([]);
    setSubProjects([]);
    setProjectActivities([]);
    setProjectActivityCosts([]);
    // Time fields
    setSelectedDates([]);
    setQuantity(1);
    // Notes field
    setNote("");
    // WageCode-related fields
    setSelectedWageCode(null);
    setSelectedWageCodeType(null);
    setIsWageTooltipOpen(false);
    // Edit mode
    setIsEditMode(false);
    setIsPrefillLocked(false);
    setShowValidation(false);
    setValidationErrors({
      dates: false,
      quantity: false,
      wageCodeType: false,
      wageCode: false,
      project: false,
      subProject: false,
      projectActivity: false,
      projectActivityCost: false,
    });
  };

  const getWageCodes = useCallback(async () => {
    try {
      const date = format(baseDate, "yyyy-MM-dd");
      const response = await fetch(
        `${rootUrl}timereport/wagecodes?date=${date}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch wage codes");
      }

      const data = await response.json();
      setWageCodes(data.wageCodes);
      setWageCodeTypes(data.wageCodeTypes);
    } catch (error) {
      console.error("Error fetching wage codes:", error);
    }
  }, [baseDate, rootUrl, accessToken]);

  const getProjects = useCallback(async () => {
    try {
      const response = await fetch(`${rootUrl}timereport/projects`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch projects");
      }

      const data = await response.json();
      const projectsData = data || [];

      setProjects(projectsData);

      setSelectedProject(null);
      setSelectedSubProject(null);
      setSelectedProjectActivity(null);
      setSelectedProjectActivityCost(null);
      setSubProjects([]);
      setProjectActivities([]);
      setProjectActivityCosts([]);

      return projectsData;
    } catch (error) {
      console.error("Error fetching projects:", error);
      return [];
    }
  }, [rootUrl, accessToken]);

  const getSubProjects = useCallback(
    async (projectId) => {
      try {
        const response = await fetch(
          `${rootUrl}timereport/projects/${projectId}/subprojects`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch subprojects");
        }

        const data = await response.json();
        const subProjectsData = data || [];

        setSubProjects(subProjectsData);
        setSelectedSubProject(null);
        setSelectedProjectActivity(null);
        setSelectedProjectActivityCost(null);
        setProjectActivities([]);
        setProjectActivityCosts([]);

        return subProjectsData;
      } catch (error) {
        console.error("Error fetching subprojects:", error);
        return [];
      }
    },
    [rootUrl, accessToken]
  );

  const getWeekDateRange = (date) => {
    const weekStart = startOfWeek(date, { weekStartsOn: 1 });
    const weekEnd = addDays(weekStart, 6);
    return {
      startDate: format(weekStart, "yyyy-MM-dd"),
      endDate: format(weekEnd, "yyyy-MM-dd"),
    };
  };

  const getProjectActivities = useCallback(
    async (projectId, subProjectId) => {
      try {
        const { startDate, endDate } = getWeekDateRange(baseDate);

        const response = await fetch(
          `${rootUrl}timereport/projects/${projectId}/subprojects/${subProjectId}/activities?startDate=${startDate}&endDate=${endDate}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch project activities");
        }

        const data = await response.json();
        const activitiesData = data || [];

        setProjectActivities(activitiesData);

        setSelectedProjectActivity(null);
        setSelectedProjectActivityCost(null);
        setProjectActivityCosts([]);

        return activitiesData;
      } catch (error) {
        console.error("Error fetching project activities:", error);
        return [];
      }
    },
    [rootUrl, accessToken, baseDate]
  );

  const getProjectActivityCosts = useCallback(
    async (projectId, subProjectId, activityId) => {
      try {
        const { startDate, endDate } = getWeekDateRange(baseDate);

        const response = await fetch(
          `${rootUrl}timereport/projects/${projectId}/subprojects/${subProjectId}/activities/${activityId}/costs?startDate=${startDate}&endDate=${endDate}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch project activity costs");
        }
        const data = await response.json();
        const costsData = data || [];

        setProjectActivityCosts(costsData);

        const defaultCost = costsData.find((cost) => cost.costCode === "TID");
        setSelectedProjectActivityCost(defaultCost || null);

        return costsData;
      } catch (error) {
        console.error("Error fetching project activity costs:", error);
        return [];
      }
    },
    [rootUrl, accessToken, baseDate]
  );

  useEffect(() => {
    if (accessToken) {
      getWageCodes();
    }
  }, [accessToken, getWageCodes]);

  useEffect(() => {
    if (baseDate) {
      setWeekDays(getWeekDays(baseDate));
    }
  }, [baseDate]);

  useEffect(() => {
    if (selectedWageCodeType) {
      const mappedWageCodes = wageCodes
        .filter((item) => item.type === selectedWageCodeType)
        .map((item) => ({
          label: `${item.code} - ${item.name}`,
          value: item.code,
          description: item.description,
        }));

      setSelectableWageCodes(mappedWageCodes);
    }
  }, [selectedWageCodeType, wageCodes]);

  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open]);

  useEffect(() => {
    if (isEditMode && editItem?.wageCode && selectableWageCodes.length > 0) {
      const matchingSelectableWageCode = selectableWageCodes.find(
        (wc) => wc.value === editItem.wageCode
      );

      if (matchingSelectableWageCode) {
        setSelectedWageCode(matchingSelectableWageCode);
      }
    }
  }, [isEditMode, editItem, selectableWageCodes]);

  useEffect(() => {
    if (editItem && open) {
      setIsEditMode(true);
      setIsPrefillLocked(false);

      if (editItem.type === "Project") {
        setSelectedTimeReportType("project");
        getProjects(true).then((projectsData) => {
          if (editItem.projectId && projectsData.length > 0) {
            const matchingProject = projectsData.find(
              (project) => project.id === editItem.projectId
            );

            let informationMissing = false;

            if (matchingProject) {
              setSelectedProject(matchingProject);

              getSubProjects(matchingProject.id, true).then(
                (subProjectsData) => {
                  if (editItem.subProjectId) {
                    const matchingSubProject = subProjectsData.find(
                      (subProject) => subProject.id === editItem.subProjectId
                    );

                    if (matchingSubProject) {
                      setSelectedSubProject(matchingSubProject);
                      getProjectActivities(
                        matchingProject.id,
                        matchingSubProject.id,
                        true
                      ).then((activitiesData) => {
                        if (editItem.projectActivityNo) {
                          const matchingActivity = activitiesData.find(
                            (activity) =>
                              activity.id === editItem.projectActivityNo
                          );
                          if (matchingActivity) {
                            setSelectedProjectActivity(matchingActivity);

                            getProjectActivityCosts(
                              matchingProject.id,
                              matchingSubProject.id,
                              matchingActivity.id,
                              true
                            ).then((costsData) => {
                              if (editItem.reportCode) {
                                const matchingCost = costsData.find(
                                  (cost) =>
                                    cost.costCode === editItem.reportCode
                                );

                                if (matchingCost) {
                                  setSelectedProjectActivityCost(matchingCost);
                                } else {
                                  informationMissing = true;
                                }
                              }
                            });
                          } else {
                            informationMissing = true;
                          }
                        }
                      });
                    } else {
                      informationMissing = true;
                    }
                  }
                }
              );
            } else {
              informationMissing = true;
            }

            if (informationMissing) {
              showToast(
                t("timeReport.addTimeDialog.errors.informationMissing"),
                "warning"
              );
            }
          }
        });
      } else {
        setSelectedTimeReportType("wage");

        if (editItem.wageCode) {
          const matchingWageCode = wageCodes.find(
            (wc) => wc.code === editItem.wageCode
          );

          if (matchingWageCode) {
            const matchingWageCodeType = wageCodeTypes.find(
              (type) => type === matchingWageCode.type
            );

            if (matchingWageCodeType) {
              setSelectedWageCodeType(matchingWageCodeType);
            }
          }
        }
      }

      setQuantity(editItem.hours || 1);
      if (editItem.date) {
        try {
          const editDate = new Date(editItem.date);
          const matchingWeekDay = weekDays.find(
            (day) =>
              day.getFullYear() === editDate.getFullYear() &&
              day.getMonth() === editDate.getMonth() &&
              day.getDate() === editDate.getDate()
          );

          if (matchingWeekDay) {
            setSelectedDates([matchingWeekDay]);
          } else {
            console.warn("Edit date not found in current week:", editDate);
          }
        } catch (error) {
          console.error("Error parsing date", error);
        }
      }

      if (editItem.note) {
        setNote(editItem.note);
      }
    } else {
      setIsEditMode(false);
      setIsPrefillLocked(false);
    }
  }, [
    editItem,
    open,
    getProjects,
    getSubProjects,
    getProjectActivities,
    getProjectActivityCosts,
    t,
    showToast,
  ]);

  useEffect(() => {
    if (!prefillData || !open) {
      return;
    }

    if (weekDays.length === 0) {
      return;
    }

    if (prefillData.type !== "Project") {
      showToast(
        t("timeReport.addTimeDialog.errors.informationMissing"),
        "warning"
      );
      onPrefillConsumed?.();
      return;
    }

    const applyPrefill = async () => {
      try {
        setIsEditMode(false);
        setIsPrefillLocked(true);
        setSelectedTimeReportType("project");
        setSelectedWageCodeType(null);
        setSelectedWageCode(null);
        setNote("");
        setShowValidation(false);
        setValidationErrors({
          dates: false,
          quantity: false,
          wageCodeType: false,
          wageCode: false,
          project: false,
          subProject: false,
          projectActivity: false,
          projectActivityCost: false,
        });
        setMessage("");

        const projectsData = await getProjects();
        const matchingProject = projectsData.find(
          (project) => String(project.id) === String(prefillData.projectId)
        );

        if (!matchingProject) {
          showToast(
            t("timeReport.addTimeDialog.errors.informationMissing"),
            "warning"
          );
          return;
        }

        setSelectedProject(matchingProject);

        const subProjectsData = await getSubProjects(matchingProject.id);
        const matchingSubProject = subProjectsData.find(
          (subProject) =>
            String(subProject.id) === String(prefillData.subProjectId)
        );

        if (!matchingSubProject) {
          showToast(
            t("timeReport.addTimeDialog.errors.informationMissing"),
            "warning"
          );
          return;
        }

        setSelectedSubProject(matchingSubProject);

        const activitiesData = await getProjectActivities(
          matchingProject.id,
          matchingSubProject.id
        );
        const matchingActivity = activitiesData.find(
          (activity) =>
            String(activity.id) === String(prefillData.projectActivityNo)
        );

        if (!matchingActivity) {
          showToast(
            t("timeReport.addTimeDialog.errors.informationMissing"),
            "warning"
          );
          return;
        }

        setSelectedProjectActivity(matchingActivity);
        await getProjectActivityCosts(
          matchingProject.id,
          matchingSubProject.id,
          matchingActivity.id
        );

        const targetDate = new Date(prefillData.date);
        const matchingWeekDay = weekDays.find((day) =>
          isSameDay(day, targetDate)
        );
        setSelectedDates(matchingWeekDay ? [matchingWeekDay] : [targetDate]);

        const hoursValue = Number(prefillData.hours);
        setQuantity(Number.isNaN(hoursValue) ? 1 : hoursValue);
      } catch (error) {
        console.error("Error applying prefill data:", error);
        showToast(t("timeReport.prefill.applyError"), "error");
      } finally {
        onPrefillConsumed?.();
      }
    };

    applyPrefill();
  }, [
    prefillData,
    open,
    weekDays,
    getProjects,
    getSubProjects,
    getProjectActivities,
    getProjectActivityCosts,
    showToast,
    t,
    onPrefillConsumed,
  ]);

  useEffect(() => {
    const fetchExistingNote = async () => {
      if (
        isEditMode ||
        selectedTimeReportType !== "wage" ||
        selectedDates.length !== 1 ||
        !accessToken
      ) {
        return;
      }
      try {
        const dateStr = format(new Date(selectedDates[0]), "yyyy-MM-dd");
        const resp = await fetch(`${rootUrl}timereport/note?date=${dateStr}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        });
        if (resp.status === 404 || resp.status === 204) {
          setNote("");
          return;
        }
        if (!resp.ok) {
          return;
        }
        const data = await resp.json().catch(() => null);
        if (data && data.note && data.note.length > 0) {
          setNote(data.note);
        } else {
          setNote("");
        }
      } catch (e) {
        // silent
      }
    };
    fetchExistingNote();
  }, [selectedDates, selectedTimeReportType, isEditMode, accessToken, rootUrl]);

  const toggleDateSelection = (date) => {
    setSelectedDates((prev) => {
      if (prev.length === 1 && prev[0].getTime() === date.getTime()) {
        return [];
      }
      return [date];
    });
  };

  const getWeekDays = (baseDate) => {
    const start = startOfWeek(baseDate, { weekStartsOn: 1 });
    return [...Array(7)].map((_, index) => addDays(start, index));
  };

  const increaseQuantity = () => {
    setQuantity((prev) => prev + 0.25);
  };

  const decreaseQuantity = () => {
    if (quantity > 0) {
      setQuantity((prev) => prev - 0.25);
    }
  };

  const handleTimeReportTypeChange = (event) => {
    const newType = event.target.value;
    setSelectedTimeReportType(newType);
    setIsWageTooltipOpen(false);

    if (newType === "wage") {
      setSelectedWageCodeType(null);
      setSelectedWageCode(null);
    } else {
      // Reset all project-related states
      setSelectedProject(null);
      setSelectedSubProject(null);
      setSelectedProjectActivity(null);
      setSelectedProjectActivityCost(null);
      setSubProjects([]);
      setProjectActivities([]);
      setProjectActivityCosts([]);

      getProjects();
    }
    // Re-evaluate validation if currently showing
    if (showValidation) {
      setValidationErrors(getValidationErrors(newType));
    }
  };

  const handleProjectChange = (event, newValue) => {
    setSelectedProject(newValue);

    setSelectedSubProject(null);
    setSelectedProjectActivity(null);
    setSelectedProjectActivityCost(null);
    setProjectActivities([]);
    setProjectActivityCosts([]);

    if (newValue && newValue.id) {
      getSubProjects(newValue.id);
    } else {
      setSubProjects([]);
    }
  };

  const handleSubProjectChange = (event, newValue) => {
    setSelectedSubProject(newValue);

    setSelectedProjectActivity(null);
    setSelectedProjectActivityCost(null);
    setProjectActivityCosts([]);

    if (selectedProject && selectedProject.id && newValue && newValue.id) {
      getProjectActivities(selectedProject.id, newValue.id);
    } else {
      setProjectActivities([]);
    }
  };

  const handleProjectActivityChange = (event, newValue) => {
    setSelectedProjectActivity(newValue);

    setSelectedProjectActivityCost(null);

    if (
      selectedProject &&
      selectedProject.id &&
      selectedSubProject &&
      selectedSubProject.id &&
      newValue &&
      newValue.id
    ) {
      getProjectActivityCosts(
        selectedProject.id,
        selectedSubProject.id,
        newValue.id
      );
    } else {
      setProjectActivityCosts([]);
    }
  };

  const handleWageCodeTypeChange = (event, newValue) => {
    setSelectedWageCodeType(newValue);
    setSelectedWageCode(null);
    setIsWageTooltipOpen(false);
    if (showValidation) setValidationErrors(getValidationErrors());
  };

  const handleWageCodeChange = (event, newValue) => {
    setSelectedWageCode(newValue);
    setIsWageTooltipOpen(false);
    if (showValidation) setValidationErrors(getValidationErrors());
  };

  const handleWageTooltipToggle = () => {
    setIsWageTooltipOpen((prev) => !prev);
  };

  const handleWageTooltipClose = () => {
    setIsWageTooltipOpen(false);
  };

  const handleProjectActivityCostChange = (event, newValue) => {
    setSelectedProjectActivityCost(newValue);
    if (showValidation) setValidationErrors(getValidationErrors());
  };

  const handleOnClose = (event, reason) => {
    if (reason !== "backdropClick") {
      resetForm();
      onClose();
    }
  };

  // Build field-level validation errors
  const getValidationErrors = (typeOverride) => {
    const type = typeOverride || selectedTimeReportType;
    const baseErrors = {
      dates: selectedDates.length === 0,
      quantity: quantity <= 0,
      wageCodeType: false,
      wageCode: false,
      project: false,
      subProject: false,
      projectActivity: false,
      projectActivityCost: false,
    };
    if (type === "wage") {
      baseErrors.wageCodeType = !selectedWageCodeType;
      baseErrors.wageCode = !selectedWageCode;
    } else {
      baseErrors.project = !selectedProject;
      baseErrors.subProject = !selectedSubProject;
      baseErrors.projectActivity = !selectedProjectActivity;
      baseErrors.projectActivityCost = !selectedProjectActivityCost;
    }
    return baseErrors;
  };

  // Detect duplicates
  const getDuplicateMessage = () => {
    // Only attempt if required minimum fields are present
    const errs = getValidationErrors();
    const hasBlockingMissing = Object.values(errs).some((v) => v);
    if (hasBlockingMissing) return "";

    const selectedDays = selectedDates.map((d) =>
      format(new Date(d), "yyyy-MM-dd")
    );

    if (selectedTimeReportType === "wage") {
      if (selectedWageCodeType && selectedWageCode) {
        for (const selectedDay of selectedDays) {
          const duplicate = reportedTimes.find(
            (report) =>
              report.wageCode === selectedWageCode.value &&
              report.date === selectedDay
          );
          if (duplicate && !isEditMode) {
            return t("timeReport.addTimeDialog.errors.duplicateWageCode", {
              label: selectedWageCode.label,
              date: selectedDay,
            });
          }
        }
      }
      return "";
    } else {
      if (
        selectedProject &&
        selectedSubProject &&
        selectedProjectActivity &&
        selectedProjectActivityCost
      ) {
        for (const selectedDay of selectedDays) {
          const duplicate = reportedTimes.find(
            (report) =>
              report.projectId === selectedProjectActivityCost.projectId &&
              report.subProjectId ===
                selectedProjectActivityCost.subProjectId &&
              report.projectActivityNo ===
                selectedProjectActivityCost.activityId &&
              report.reportCode === selectedProjectActivityCost.costCode &&
              report.date === selectedDay
          );
          if (duplicate && !isEditMode) {
            return t(
              "timeReport.addTimeDialog.errors.duplicateProjectActivity",
              {
                costCode: selectedProjectActivityCost.costCode,
                projectId: selectedProjectActivityCost.projectId,
                subProjectId: selectedProjectActivityCost.subProjectId,
                activityId: selectedProjectActivityCost.activityId,
                date: selectedDay,
              }
            );
          }
        }
      }
      return "";
    }
  };

  useEffect(() => {
    const msg = getDuplicateMessage();
    setMessage(msg);
    if (showValidation) {
      setValidationErrors(getValidationErrors());
    }
  }, [
    selectedDates,
    quantity,
    selectedTimeReportType,
    selectedWageCodeType,
    selectedWageCode,
    selectedProject,
    selectedSubProject,
    selectedProjectActivity,
    selectedProjectActivityCost,
    reportedTimes,
    showValidation,
  ]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Validate required fields
      const errs = getValidationErrors();
      setValidationErrors(errs);
      setShowValidation(true);
      const hasErrors = Object.values(errs).some((v) => v);
      if (hasErrors) {
        setIsSubmitting(false);
        return;
      }

      // Prevent submit on duplicates
      const duplicateMsg = getDuplicateMessage();
      if (duplicateMsg) {
        setMessage(duplicateMsg);
        setIsSubmitting(false);
        return;
      }

      const endpoint =
        selectedTimeReportType === "wage"
          ? `${rootUrl}timereport/wage`
          : `${rootUrl}timereport/project`;

      const isEdit = isEditMode && editItem;
      const method = isEdit ? "PUT" : "POST";

      let requestBody;

      if (selectedTimeReportType === "wage") {
        requestBody = {
          wageCodeType: selectedWageCodeType,
          wageCode: selectedWageCode?.value,
          quantity,
          dates: selectedDates.map((date) => format(date, "yyyy-MM-dd")),
          note,
          objId: editItem?.objId || null,
          objVersion: editItem?.objVersion || null,
          name:
            wageCodes.find((code) => code.code === selectedWageCode?.value)
              ?.name || "",
        };
      } else {
        requestBody = {
          projectId: selectedProject?.id,
          subProjectId: selectedSubProject?.id,
          activityId: selectedProjectActivity?.id,
          quantity,
          dates: selectedDates.map((date) => format(date, "yyyy-MM-dd")),
          note,
          reportCostCode: selectedProjectActivityCost.costCode,
          reportCostType: selectedProjectActivityCost.costType,
          objId: editItem?.objId || null,
          objVersion: editItem?.objVersion || null,
          name: selectedProject?.name || "",
          description: selectedProjectActivityCost.name || "",
        };
      }

      const response = await fetch(endpoint, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(requestBody),
      });

      const responseData = await response.json();

      if (responseData.isError || responseData.isSuccess === false) {
        const errorMessage = parseError(responseData, t);
        showToast(
          errorMessage ||
            (isEdit
              ? t("timeReport.editTimeDialog.errors.submitError")
              : t("timeReport.addTimeDialog.errors.submitError")),
          "error"
        );
        setIsSubmitting(false);
        return;
      }

      const submittedTimeReports = responseData.submittedTimeReports;

      // If we're editing a confirmed item, remove the confirmation
      if (isEdit && editItem.status === "Confirmed") {
        const removedConfirmationResponse = await fetch(
          `${rootUrl}timereport/confirmdates`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify([editItem.date]),
          }
        );

        if (!removedConfirmationResponse.ok) {
          console.error("Failed to remove confirmation status");
        }
      }

      if (onSave) {
        onSave(submittedTimeReports, isEdit);
      }

      showToast(
        isEdit
          ? t("timeReport.editTimeDialog.successMessage")
          : t("timeReport.addTimeDialog.successMessage"),
        "success"
      );
      resetForm();
      onClose();
    } catch (error) {
      console.error("Error submitting time report:", error);
      showToast(t("timeReport.addTimeDialog.errors.submitError"), "error");
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={handleOnClose}
      maxWidth="sm"
      fullWidth={true}
      fullScreen={window.innerWidth < 600}
      slots={{ backdrop: Backdrop }}
    >
      <DialogTitle>
        {isEditMode
          ? t("timeReport.editTimeDialog.title")
          : t("timeReport.addTimeDialog.title")}{" "}
        - {t("week")}&nbsp;
        {getISOWeek(baseDate)}
      </DialogTitle>
      <DialogContent>
        {/* Type */}
        <Box sx={{ minWidth: 220, marginTop: 2 }}>
          <TextField
            fullWidth
            select
            label={t("timeReport.addTimeDialog.type")}
            value={selectedTimeReportType}
            onChange={handleTimeReportTypeChange}
            variant="outlined"
            SelectProps={{ native: true }}
            disabled={isEditMode || isPrefillLocked}
          >
            <option value="wage">
              {t("timeReport.addTimeDialog.types.wage")}
            </option>
            <option value="project">
              {t("timeReport.addTimeDialog.types.project")}
            </option>
          </TextField>
        </Box>
        {selectedTimeReportType === "wage" ? (
          <>
            {/* Wage Type Code */}
            <Box sx={{ minWidth: 220, marginTop: 3 }}>
              <Autocomplete
                options={wageCodeTypes}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("timeReport.addTimeDialog.wageTypeCode")}
                    variant="outlined"
                    error={showValidation && validationErrors.wageCodeType}
                  />
                )}
                value={selectedWageCodeType}
                onChange={handleWageCodeTypeChange}
                getOptionLabel={(option) =>
                  option
                    ? t(`timeReport.addTimeDialog.wageCodeGroups.${option}`)
                    : ""
                }
                disabled={isEditMode}
              />
            </Box>

            {/* Wage Type Name */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                minWidth: 220,
                marginTop: 3,
              }}
            >
              <Autocomplete
                options={selectableWageCodes}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("timeReport.addTimeDialog.wageTypeName")}
                    variant="outlined"
                    error={showValidation && validationErrors.wageCode}
                  />
                )}
                value={selectedWageCode}
                onChange={handleWageCodeChange}
                sx={{ flex: 1 }}
                disabled={isEditMode}
              />
              <Box
                sx={{
                  width: 40,
                  display: "flex",
                  justifyContent: "center",
                  marginLeft: 1,
                }}
              >
                {selectedWageCode?.description && (
                  <ClickAwayListener onClickAway={handleWageTooltipClose}>
                    <Tooltip
                      title={selectedWageCode.description}
                      arrow
                      open={isWageTooltipOpen}
                      onClose={handleWageTooltipClose}
                      disableFocusListener
                      disableHoverListener
                      disableTouchListener
                    >
                      <IconButton onClick={handleWageTooltipToggle}>
                        <InfoOutlinedIcon />
                      </IconButton>
                    </Tooltip>
                  </ClickAwayListener>
                )}
              </Box>
            </Box>
          </>
        ) : (
          <>
            {/* Project */}
            <Box sx={{ minWidth: 220, marginTop: 3 }}>
              <Autocomplete
                options={projects}
                getOptionLabel={(option) => option.name || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("timeReport.addTimeDialog.project")}
                    variant="outlined"
                    error={showValidation && validationErrors.project}
                  />
                )}
                value={selectedProject}
                onChange={handleProjectChange}
                getOptionKey={(option) => option.id}
                disabled={isEditMode || isPrefillLocked}
              />
            </Box>
            {/* SubProject */}
            <Box sx={{ minWidth: 220, marginTop: 3 }}>
              <Autocomplete
                options={subProjects}
                getOptionLabel={(option) => option.name || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("timeReport.addTimeDialog.subProject")}
                    variant="outlined"
                    error={showValidation && validationErrors.subProject}
                  />
                )}
                value={selectedSubProject}
                onChange={handleSubProjectChange}
                disabled={isEditMode || isPrefillLocked || !selectedProject}
              />
            </Box>
            {/* Project Activity */}
            <Box sx={{ minWidth: 220, marginTop: 3 }}>
              <Autocomplete
                options={projectActivities}
                getOptionLabel={(option) => option.name || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("timeReport.addTimeDialog.projectActivity")}
                    variant="outlined"
                    error={showValidation && validationErrors.projectActivity}
                  />
                )}
                value={selectedProjectActivity}
                onChange={handleProjectActivityChange}
                disabled={isEditMode || isPrefillLocked || !selectedSubProject}
              />
            </Box>
            {/* Project Activity Cost */}
            <Box sx={{ minWidth: 220, marginTop: 3 }}>
              <Autocomplete
                options={projectActivityCosts}
                getOptionLabel={(option) => option.name || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("timeReport.addTimeDialog.projectActivityCost")}
                    variant="outlined"
                    error={
                      showValidation && validationErrors.projectActivityCost
                    }
                  />
                )}
                value={selectedProjectActivityCost}
                onChange={handleProjectActivityCostChange}
                disabled={isEditMode || !selectedProjectActivity}
              />
            </Box>
          </>
        )}
        {/* Quantity Counter */}
        <Box sx={{ minWidth: 220, marginTop: 2 }}>
          <Grid container alignItems="center" spacing={1}>
            <Grid item>
              <IconButton
                onClick={decreaseQuantity}
                disabled={quantity <= 0.25}
              >
                <RemoveIcon />
              </IconButton>
            </Grid>
            <Grid item>
              <TextField
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(0, +e.target.value))}
                inputProps={{ step: 0.25, min: 0 }}
                sx={{ width: "85px", textAlign: "center" }}
                error={showValidation && validationErrors.quantity}
              />
            </Grid>
            <Grid item>
              <IconButton onClick={increaseQuantity}>
                <AddIcon />
              </IconButton>
            </Grid>
          </Grid>
          <Box sx={{ marginTop: 1 }}>
            {(() => {
              const hours = Math.floor(quantity);
              const minutes = Math.round((quantity - hours) * 60);
              return t("timeReport.addTimeDialog.duration", {
                hours,
                minutes,
                defaultValue: `${hours} hours and ${minutes} minutes`,
              });
            })()}
          </Box>
        </Box>
        {/* Reporting Date */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "8px",
            marginTop: 3,
            border:
              showValidation && validationErrors.dates ? "1px solid" : "none",
            borderColor:
              showValidation && validationErrors.dates
                ? "error.main"
                : "transparent",
            borderRadius: 1,
            padding: 1,
          }}
        >
          {weekDays.map((date, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                marginLeft: "2px",
                marginRight: "2px",
              }}
            >
              <Typography variant="body2" sx={{ marginBottom: "4px" }}>
                {dayNames[index]}
              </Typography>
              <Chip
                label={format(date, "dd")}
                onClick={() =>
                  !isEditMode && !isPrefillLocked && toggleDateSelection(date)
                }
                color={
                  selectedDates.some(
                    (selectedDate) => selectedDate.getTime() === date.getTime()
                  )
                    ? "primary"
                    : isWeekend(date)
                      ? "secondary"
                      : "default"
                }
                sx={{
                  fontWeight: selectedDates.some(
                    (selectedDate) => selectedDate.getTime() === date.getTime()
                  )
                    ? "bold"
                    : "normal",
                  fontSize: "16px",
                  minWidth: "unset",
                  cursor: isEditMode || isPrefillLocked ? "default" : "pointer",
                  opacity: isEditMode || isPrefillLocked ? 0.7 : 1,
                }}
              />
            </Box>
          ))}
        </Box>
        {/* Optional Comment - only shown for wage type */}
        {selectedTimeReportType === "wage" && (
          <Box sx={{ minWidth: 220, marginTop: 3 }}>
            <TextField
              label={t("timeReport.addTimeDialog.comment")}
              variant="outlined"
              multiline
              rows={3}
              fullWidth
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </Box>
        )}
        <Typography
          variant="body2"
          sx={{ marginBottom: "4px", marginTop: "8px" }}
        >
          {message}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          onClick={handleSubmit}
          disabled={isSubmitting}
          loading={isSubmitting}
        >
          {isEditMode ? t("update") : t("add")}
        </Button>
        <Button onClick={handleOnClose} color="primary">
          {t("close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportTimeModal;
